import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Save, Target, Trophy, Zap, Swords, Crown, ListChecks } from "lucide-react";
import ThirdPlacePlayoffCard from "@/components/ThirdPlacePlayoffCard";
import PowerPickSettingsCard from "@/components/PowerPickSettingsCard";
import { isGolfFormat, isGolfPickem, isSeasonFormat } from "@/lib/tournamentFormat";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import { isPotPlayoffsTemplate, POT_PLAYOFFS_LABELS } from "@/lib/potPlayoffsTemplates";
import { isFootballPlayoffsTemplate, FOOTBALL_PLAYOFFS_LABELS } from "@/lib/footballPlayoffsTemplates";

// iter87.112 — Boost-only template (nfl_boost_api).
// Its entire game surface is the weekly boost picker; the season
// Win/Draw/Loss + League Champion + Power Pick + Weekly Spread
// Challenge toggle cards make no sense there. Only knob: picks per
// week (1..25), locked once first kickoff passes.
// iter87.199.162 — EPL/La Liga Weekly Spread joins the boost-only set:
// same shape as NFL Weekly Pick'em (boost IS the whole game).
// Set now imported from the shared constant (see lib/boostOnlyTemplates.js).
const isBoostOnlyTournament = (t) => isBoostOnlyTemplate(t?.template);
const BOOST_ONLY_LABELS = {
    nfl_boost_api: "NFL Weekly Pick\u2019em",
    epl_boost_api: "Premier League Weekly Pick\u2019em",
    la_liga_boost_api: "La Liga Weekly Pick\u2019em",
};

// iter87.199.77 — Template-aware picks-per-week default (mirrors
// backend `_picks_per_round` in `matchday_boost.py`). Operator lock
// 2026-02-16: NFL = 8. Admin caps at 25 upstream so a
// full weekly slate (16 NFL games) can be picked.
const PICKS_PER_ROUND_MAX = 25;
const _defaultPicksPerRound = (tpl) =>
    tpl === "nfl_boost_api" ? 8
        : (tpl === "epl_boost_api" || tpl === "la_liga_boost_api") ? 5 : 8;

// iter87.199.79 — Shared header + divider that groups all "optional
// scoring extras" (Third-place playoff, Matchday Boost toggle, Power
// Pick) under a single visual bucket so the tab reads as
// `Core scoring → Boosts & Extras` instead of an ad-hoc stack.
// Applies uniformly across every non-boost-only template.
function BoostsExtrasSection({ children }) {
    return (
        <section
            className="pt-2 space-y-5"
            data-testid="scoring-boosts-extras-section"
        >
            <div className="flex items-center gap-3 pt-4 border-t-2 border-zinc-800">
                <div className="w-6 h-6 rounded-md bg-volt/10 border border-volt/40 flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-volt" />
                </div>
                <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-volt font-black">
                        Boosts & Extras
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-snug">
                        Optional scoring levers on top of the core game.
                    </p>
                </div>
            </div>
            <div className="space-y-5">
                {children}
            </div>
        </section>
    );
}

// iter87.212.22 — Generic, tournament-scoped heads-up (operator-requested
// home for this note: Scoring tab, NOT the rivalry-level Crewmates panel,
// since exclusion is a per-tournament fact, not a rivalry-wide one).
// Covers both reasons a crewmate can be missing from THIS tournament's
// leaderboard: joined after it locked, or (pot pick'em only) never
// submitted picks in time. No per-player detail — just the heads-up.
function LeaderboardExclusionNote() {
    return (
        <p
            data-testid="scoring-leaderboard-exclusion-note"
            className="text-[10px] text-zinc-500 leading-relaxed"
        >
            Heads-up — a crewmate who joins after this tournament locks, or never submits their picks in time, won't show up on this tournament's leaderboard. They stay in the rivalry and can still play other tournaments normally.
        </p>
    );
}

function BoostOnlyScoringCard({ tournament, isLocked, onChanged }) {
    const templateDefault = _defaultPicksPerRound(tournament?.template);
    const initial = tournament?.matchday_boost?.picks_per_round ?? templateDefault;
    // iter87.199.79 — Use a raw string state so the user can DELETE the
    // current value and retype freely (was previously clamped on every
    // keystroke, making it impossible to erase a starting "10").
    // The int value used for save is derived on blur / save-time.
    const [picksRaw, setPicksRaw] = useState(String(initial));
    const picksInt = parseInt(picksRaw, 10);
    const picksValid = Number.isInteger(picksInt) && picksInt >= 1 && picksInt <= PICKS_PER_ROUND_MAX;
    const [saving, setSaving] = useState(false);
    // iter87.199.163 — Real lock state now comes from the backend
    // (`picks_per_round_locked` on the boost-state response), which
    // correctly accounts for mid-season `pending_start` leagues and
    // the `start_week` cutover — the old client-side guess off
    // `scheduled_first_kickoff_at` (a field these templates don't even
    // carry) left the input enabled until save-time 409'd.
    const [seasonLocked, setSeasonLocked] = useState(false);
    const disabled = isLocked || seasonLocked || saving;
    // iter87.199.161 — "Start Tournament: Week X" for mid-season
    // creates. `pending_start` is already on the tournament doc; the
    // week candidate needs a live fetch (it's "whatever's live right
    // now", not a stored value until Start is actually clicked).
    const pendingStart = !!tournament?.matchday_boost?.pending_start;
    const [weekCandidate, setWeekCandidate] = useState(null);
    const [startingTournament, setStartingTournament] = useState(false);
    useEffect(() => {
        if (!tournament?.id) return;
        api.getMatchdayBoostState(tournament.id)
            .then((d) => {
                if (pendingStart) setWeekCandidate(d?.start_week_candidate ?? null);
                setSeasonLocked(!!d?.picks_per_round_locked);
            })
            .catch(() => {});
    }, [pendingStart, tournament?.id]);
    const handleStart = async () => {
        setStartingTournament(true);
        try {
            const r = await api.startBoostTournament(tournament.id);
            toast.success(`Tournament started — Week ${r.start_week} is live ⚡`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not start the tournament");
        } finally {
            setStartingTournament(false);
        }
    };
    const handleSave = async () => {
        if (!picksValid) {
            toast.error(`Picks per week must be between 1 and ${PICKS_PER_ROUND_MAX}`);
            return;
        }
        setSaving(true);
        try {
            await api.updateMatchdayBoostPicksPerRound(tournament.id, picksInt);
            toast.success(`Locked in ${picksInt} picks per week`);
            onChanged?.();
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 409) {
                // iter87.199.80 — Drift guard fires when the admin
                // tries to lower `picks_per_round` while crewmates
                // already have more picks than the new cap in an
                // upcoming (unlocked) week. Surface the diagnostic
                // message verbatim so the admin knows who's stuck.
                if (detail && typeof detail === "object" && detail.message) {
                    toast.error(detail.message, { duration: 8000 });
                } else if (typeof detail === "string" && detail.includes("First kickoff")) {
                    setSeasonLocked(true);
                    toast.error("First kickoff has passed — locked for the season");
                } else {
                    toast.error(typeof detail === "string" ? detail : "Save failed");
                }
            } else {
                toast.error((typeof detail === "string" ? detail : detail?.message) || "Save failed");
            }
        } finally {
            setSaving(false);
        }
    };
    const scoringMode = tournament?.matchday_boost?.scoring_mode;
    const isSpread = scoringMode === "spread";
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="boost-only-scoring-card">
            {pendingStart && (
                <div className="brutal-card-light p-3 space-y-2 border-volt/40" data-testid="scoring-tab-start-tournament-card">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-volt">
                        🏁 Not started yet
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Invite your crew, then start it whenever you're ready — picks open from Week {weekCandidate ?? "—"} onward.
                    </p>
                    <button
                        type="button"
                        onClick={handleStart}
                        disabled={startingTournament}
                        data-testid="scoring-tab-start-tournament-btn"
                        className="brutal-btn-primary disabled:opacity-40"
                    >
                        <Flag className="w-4 h-4" /> {startingTournament ? "Starting…" : `Start Tournament: Week ${weekCandidate ?? "—"}`}
                    </button>
                </div>
            )}
            <div className="flex items-start gap-2">
                <Zap className="w-5 h-5 text-volt shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h2 className="font-display font-extrabold text-xl uppercase" data-testid="boost-only-scoring-title">
                        Scoring — {BOOST_ONLY_LABELS[tournament?.template] || "Weekly Pick\u2019em"}
                    </h2>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                        This template's scoring engine is <b className="text-cyber">locked at 6 / 2 / 0 / −2</b> — win and cover +6, win without covering +2, loss that still covers 0, miss both −2. There are no season-level Win/Draw/Loss points, no League Champion bonus, and no Power Pick — the weekly boost picker IS the whole game.
                    </p>
                    <div className="mt-2">
                        <LeaderboardExclusionNote />
                    </div>
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt">
                    ⚡ {isSpread ? "Weekly Spread Challenge" : "Weekly Boost"} · Always On
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Boost cannot be turned off on this template — it is the entire game surface.
                </p>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="boost-only-picks-per-round-card">
                <label className="block text-[10px] uppercase font-bold text-zinc-500">
                    Picks per week
                </label>
                <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={picksRaw}
                    disabled={disabled}
                    onChange={(e) => setPicksRaw(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                    onBlur={() => {
                        // On blur, normalise: empty or out-of-range snaps to
                        // the template default so we never save "" or 0.
                        if (picksRaw === "" || !picksValid) setPicksRaw(String(templateDefault));
                    }}
                    className="brutal-input w-24 font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="boost-only-picks-per-round-input"
                />
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    How many spread bets each crewmate makes per week (1–{PICKS_PER_ROUND_MAX}). Weeks with fewer games automatically play at that lower number. {seasonLocked
                        ? <><b className="text-heat">Locked for the season</b> — the first game since your league started has kicked off.</>
                        : <>Locks once the first game since your league started kicks off.</>}
                </p>
            </div>
            <button
                data-testid="boost-only-save-picks-per-round-btn"
                onClick={handleSave}
                disabled={disabled || !picksValid || picksInt === initial}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> Save picks per week
            </button>
        </div>
    );
}

// iter87.227 — Dedicated scoring card for the two "pot playoffs"
// templates (MLB/NBA Playoffs). Was previously falling through to the
// generic soccer/World-Cup scoring form below — stage-decay, third
// place, group stage, bracket-advancement reach points — none of
// which apply here (user-reported bug). Team Pick Pot is the only
// admin-editable knob (win/loss per game); Champions Pick and Series
// Predictor are flat engine constants (mlb_engine.py / nba_engine.py),
// shown read-only so admins understand exactly how points are earned.
function PotPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const label = POT_PLAYOFFS_LABELS[tournament?.template] || "Playoffs";
    const champLabel = tournament?.template === "nba_playoffs_api" ? "NBA Finals" : "World Series";
    const winPoints = scoring?.win_points ?? 6;
    const lossPoints = scoring?.loss_points ?? -2;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <>
            <div className="brutal-card p-5 space-y-4" data-testid="pot-playoffs-scoring-card">
                <div>
                    <h2 className="font-display font-extrabold text-xl uppercase" data-testid="pot-playoffs-scoring-title">
                        Scoring — {label}
                    </h2>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                        Three ways to score: <b className="text-cyber">Team Pick Pot</b> (per-game W/L on your drafted teams), <b className="text-volt">Champions Pick</b> (flat bonus), and <b className="text-heat">Series Predictor</b> (per-series team + game-count picks).
                    </p>
                    <div className="mt-2"><LeaderboardExclusionNote /></div>
                </div>
                <div className="brutal-card-light p-3 space-y-2" data-testid="pot-playoffs-team-pick-pot-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                        <Zap className="w-3 h-3" /> Team Pick Pot · Per Game
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Every playoff game your drafted teams play scores instantly — no bracket-advancement points, just wins and losses.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <div data-testid="pot-playoffs-win-points">
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Win (per game)</label>
                            <input
                                data-testid="pot-playoffs-win-points-input"
                                type="number"
                                value={winPoints}
                                disabled={isLocked}
                                onChange={(e) => setScoring({ ...scoring, win_points: parseInt(e.target.value) || 0 })}
                                className="brutal-input w-full font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                        </div>
                        <div data-testid="pot-playoffs-loss-points">
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Loss (per game)</label>
                            <input
                                data-testid="pot-playoffs-loss-points-input"
                                type="number"
                                value={lossPoints}
                                disabled={isLocked}
                                onChange={(e) => setScoring({ ...scoring, loss_points: parseInt(e.target.value) || 0 })}
                                className="brutal-input w-full font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                            />
                        </div>
                    </div>
                </div>
                <div className="brutal-card-light p-3 space-y-2" data-testid="pot-playoffs-champions-pick-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                        <Trophy className="w-3 h-3" /> Champions Pick · Flat Bonus
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-relaxed">
                        <b className="text-zinc-300">+10 points</b> for correctly picking the {champLabel} champion. Awarded the moment the champion is decided — fixed engine value, not editable per tournament.
                    </p>
                </div>
                <div className="brutal-card-light p-3 space-y-2" data-testid="pot-playoffs-series-predictor-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                        <Target className="w-3 h-3" /> Series Predictor · Per Series
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-relaxed">
                        <b className="text-zinc-300">+6</b> for picking the series-winning team (<b className="text-zinc-300">+4 bonus</b> if you also nail the exact deciding game) · <b className="text-zinc-300">−2</b> for a wrong team pick (<b className="text-zinc-300">+2 partial credit</b> if the game count alone is right, netting 0). Fixed engine value, not editable per tournament.
                    </p>
                </div>
                <button
                    data-testid="pot-playoffs-save-scoring-btn"
                    onClick={handleSave}
                    disabled={isLocked || saving}
                    className="brutal-btn-primary disabled:opacity-40"
                >
                    <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
                </button>
            </div>
            <BoostsExtrasSection>
                <PowerPickSettingsCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
            </BoostsExtrasSection>
        </>
    );
}

// iter87.230 — Dedicated scoring card for NFL/CFB Playoffs. Round
// points (6/8/10/12) and pot picks/multipliers are FIXED engine rules
// (see football_playoffs_core.py) — no per-game W/L knob like MLB/NBA,
// no Champions Pick, no Series Predictor. Only editable knob is Power
// Pick (on/off — multipliers are pot-fixed, same as NFL Season).
function FootballPlayoffsScoringCard({ tournament, isLocked, onChanged }) {
    const label = FOOTBALL_PLAYOFFS_LABELS[tournament?.template] || "Playoffs";
    const rounds = (tournament?.[tournament?.template === "nfl_playoffs_api" ? "nfl" : "cfb"]?.rounds) || [];
    return (
        <>
            <div className="brutal-card p-5 space-y-4" data-testid="football-playoffs-scoring-card">
                <div>
                    <h2 className="font-display font-extrabold text-xl uppercase" data-testid="football-playoffs-scoring-title">
                        Scoring — {label}
                    </h2>
                    <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                        <b className="text-cyber">Playoff Picks</b> (1 from Pot 1, 2 from Pot 2, 2 from Pot 3 — scored by round) plus
                        the <b className="text-volt">Playoff Predictor</b> between rounds (every game in the round — spread + over/under). No Champions Pick, no Series Predictor.
                    </p>
                    <div className="mt-2"><LeaderboardExclusionNote /></div>
                </div>
                <div className="brutal-card-light p-3 space-y-2" data-testid="football-playoffs-round-points-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                        <Zap className="w-3 h-3" /> Round Win Points — Fixed
                    </p>
                    <p className="text-[10px] text-zinc-500 leading-relaxed">
                        Every drafted team's playoff win scores instantly — points increase each round. Fixed engine values, not editable per tournament.
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-1">
                        {(rounds.length ? rounds : [
                            { label: "Wild Card / First Round", points: 6 },
                            { label: "Divisional / Quarterfinal", points: 8 },
                            { label: "Conference / Semifinal", points: 10 },
                            { label: "Super Bowl / Championship", points: 12 },
                        ]).map((r) => (
                            <li key={r.key || r.label} className="flex items-center justify-between border-b border-white/[0.04] py-1 last:border-0">
                                <span>{r.label}</span>
                                <span className="font-mono font-bold text-cyber">+{r.points}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                {/* iter87.370 — Playoff Predictor now grades TWO picks per
                    game: spread (unchanged) + a new Over/Under pick.
                    Fixed engine values, not editable per tournament. */}
                <div className="brutal-card-light p-3 space-y-2" data-testid="playoff-predictor-scoring-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                        <Zap className="w-3 h-3" /> Playoff Predictor — Fixed
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-1">
                        <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                            <span>Spread · win + cover</span><span className="font-mono font-bold text-volt">+6</span>
                        </li>
                        <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                            <span>Spread · win, no cover</span><span className="font-mono font-bold text-cyber">+2</span>
                        </li>
                        <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                            <span>Spread · loss but covers</span><span className="font-mono font-bold text-zinc-400">0</span>
                        </li>
                        <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                            <span>Spread · miss both</span><span className="font-mono font-bold text-heat">-2</span>
                        </li>
                        <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                            <span>Over/Under · correct</span><span className="font-mono font-bold text-cyber">+4</span>
                        </li>
                        <li className="flex items-center justify-between py-1">
                            <span>Over/Under · miss (push counts as a miss)</span><span className="font-mono font-bold text-heat">-2</span>
                        </li>
                    </ul>
                </div>
            </div>
            <BoostsExtrasSection>
                <PowerPickSettingsCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
            </BoostsExtrasSection>
        </>
    );
}

// iter87.361 — Dedicated scoring card for NWSL Playoffs. Pure
// pick'em — NO Team Pick Pot at all (unlike MLB/NBA above). Champions
// Pick + Dark Horse Pick points ARE admin-editable (operator request);
// Match Predictor (Winner/Decided-by/Goals) is a fixed engine rule
// (nwslplayoffs_engine.py), shown read-only.
function NwslPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const champPoints = scoring?.nwsl_champion_points ?? 15;
    const champHedgePoints = scoring?.nwsl_champion_hedge_points ?? 5;
    const dhPoints = scoring?.nwsl_darkhorse_win_points ?? 5;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="nwsl-playoffs-scoring-card">
            <div>
                <h2 className="font-display font-extrabold text-xl uppercase" data-testid="nwsl-playoffs-scoring-title">
                    Scoring — NWSL Playoffs
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                    Pure pick'em — no draft, no rostered teams. Three mechanics: <b className="text-volt">NWSL Champion</b> (1-2 picks,
                    hedge cost if picking 2), <b className="text-heat">Dark Horse Pick</b> (per-win bonus), and the <b className="text-cyber">Playoff Predictor</b> (fixed per-game scoring).
                </p>
                <div className="mt-2"><LeaderboardExclusionNote /></div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nwsl-champion-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                    <Trophy className="w-3 h-3" /> NWSL Champion
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick 1 or 2 of the 8 bracket teams, locked at Round 1 kickoff. Picking 2 costs you the hedge rate below if one wins.
                </p>
                <div data-testid="nwsl-champion-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (1 pick)</label>
                    <input
                        data-testid="nwsl-champion-points-input"
                        type="number"
                        value={champPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nwsl_champion_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="nwsl-champion-hedge-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (2 picks — hedge)</label>
                    <input
                        data-testid="nwsl-champion-hedge-points-input"
                        type="number"
                        value={champHedgePoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nwsl_champion_hedge_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nwsl-darkhorse-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                    <Flag className="w-3 h-3" /> Dark Horse Pick · Per Win
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    A seed 5-8 team only, locked alongside NWSL Champion. Stacks for every playoff game that team wins.
                </p>
                <div data-testid="nwsl-darkhorse-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per win</label>
                    <input
                        data-testid="nwsl-darkhorse-points-input"
                        type="number"
                        value={dhPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nwsl_darkhorse_win_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nwsl-predictor-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                    <Target className="w-3 h-3" /> Playoff Predictor · Fixed, Per Game
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    <b className="text-zinc-300">+3</b> for the correct Winner · <b className="text-zinc-300">+2</b> for how it's decided
                    (Regulation / Extra Time / Penalties) · <b className="text-zinc-300">+5</b> for Total Goals Over/Under a fixed 2.5 line
                    (shootout goals don't count). Each game locks at its own kickoff — fixed engine values, not editable per tournament.
                </p>
            </div>
            <button
                data-testid="nwsl-playoffs-save-scoring-btn"
                onClick={handleSave}
                disabled={isLocked || saving}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
            </button>
        </div>
    );
}

// iter87.465.20 — Dedicated scoring card for MLS Playoffs (user-
// approved spec). Pure pick'em — NO Team Pick Pot. Champions Pick +
// Dark Horse Pick + Play-In Bonus points ARE admin-editable; Series
// Predictor (Round One) and the reused Match Predictor (Conf
// Semis+) are fixed engine rules, shown read-only.
function MlsPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const champPoints = scoring?.mls_champion_points ?? 15;
    const champHedgePoints = scoring?.mls_champion_hedge_points ?? 5;
    const dhPoints = scoring?.mls_darkhorse_win_points ?? 5;
    const playinPoints = scoring?.mls_playin_correct_points ?? 6;
    const playinIncorrectPoints = scoring?.mls_playin_incorrect_points ?? -2;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="mls-playoffs-scoring-card">
            <div>
                <h2 className="font-display font-extrabold text-xl uppercase" data-testid="mls-playoffs-scoring-title">
                    Scoring — MLS Playoffs
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                    Pure pick'em — no draft. Five pre/in-season mechanics: <b className="text-volt">MLS Champion</b> (1-2 picks),
                    <b className="text-heat"> Dark Horse Pick</b> (per-win), <b className="text-cyber"> Play-In Bonus</b> (per conference),
                    and the fixed <b className="text-zinc-300">Series Predictor</b> (Round One) + <b className="text-zinc-300">Playoff Predictor</b> (Conf Semis+) below.
                </p>
                <div className="mt-2"><LeaderboardExclusionNote /></div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="mls-champion-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                    <Crown className="w-3 h-3" /> MLS Champion
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick 1 or 2 playoff-field teams, locked at Wild Card kickoff. Picking 2 costs you the hedge rate below if one wins.
                </p>
                <div data-testid="mls-champion-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (1 pick)</label>
                    <input
                        data-testid="mls-champion-points-input"
                        type="number"
                        value={champPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, mls_champion_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="mls-champion-hedge-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (2 picks — hedge)</label>
                    <input
                        data-testid="mls-champion-hedge-points-input"
                        type="number"
                        value={champHedgePoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, mls_champion_hedge_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="mls-darkhorse-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                    <Swords className="w-3 h-3" /> Dark Horse Pick · Per Win
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    A seed 6-9 team only (own conference), locked alongside MLS Champion/Play-In. Stacks for every playoff game that team wins.
                </p>
                <div data-testid="mls-darkhorse-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per win</label>
                    <input
                        data-testid="mls-darkhorse-points-input"
                        type="number"
                        value={dhPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, mls_darkhorse_win_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="mls-playin-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                    <ListChecks className="w-3 h-3" /> Play-In Bonus · Per Conference
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick the Wild Card winner in EACH conference, locked alongside MLS Champion/Dark Horse. Flat bonus — not part of the in-season Series Predictor.
                </p>
                <div data-testid="mls-playin-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per correct conference</label>
                    <input
                        data-testid="mls-playin-points-input"
                        type="number"
                        value={playinPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, mls_playin_correct_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="mls-playin-incorrect-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per incorrect conference</label>
                    <input
                        data-testid="mls-playin-incorrect-points-input"
                        type="number"
                        value={playinIncorrectPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, mls_playin_incorrect_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="mls-series-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 flex items-center gap-1.5">
                    <Target className="w-3 h-3" /> Series Predictor (Round One) · Fixed
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    <b className="text-zinc-300">+6</b> correct series winner · <b className="text-zinc-300">+10</b> winner AND exact deciding game ·
                    <b className="text-zinc-300"> −2</b> wrong winner · <b className="text-zinc-300">0</b> wrong winner but exact deciding game.
                    Best-of-3 only, 16 teams total. Fixed engine values, not editable per tournament.
                </p>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="mls-boost-note-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Playoff Predictor (Conf Semis+) · Fixed
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Conf Semis, Conf Finals and MLS Cup reuse the standard Playoff Predictor spread + Over/Under mechanic (same values as NFL/CFB Playoffs) — see the Boosts &amp; Extras section.
                </p>
            </div>
            <button
                data-testid="mls-playoffs-save-scoring-btn"
                onClick={handleSave}
                disabled={isLocked || saving}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
            </button>
        </div>
    );
}

// iter87.466.1 — Dedicated scoring card for UCL Playoffs (user-
// approved spec). Pure pick'em — NO Team Pick Pot. UCL Champion +
// Dark Horse Pick points ARE admin-editable; Advance Predictor and
// Champions Predictor (Final) are fixed engine rules, shown read-only.
function UclPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const champPoints = scoring?.ucl_champion_points ?? 20;
    const champHedgePoints = scoring?.ucl_champion_hedge_points ?? 8;
    const dhOuterPoints = scoring?.ucl_darkhorse_outer_tier_points ?? 10;
    const dhInnerPoints = scoring?.ucl_darkhorse_inner_tier_points ?? 5;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="ucl-playoffs-scoring-card">
            <div>
                <h2 className="font-display font-extrabold text-xl uppercase" data-testid="ucl-playoffs-scoring-title">
                    Scoring — UCL Playoffs
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                    Pure pick'em — no draft. <b className="text-volt">UCL Champion</b> (1-2 picks),
                    <b className="text-heat"> Dark Horse Pick</b> (tiered, per tie advanced), and the fixed
                    <b className="text-zinc-300"> Two-Leg Predictor</b> + <b className="text-zinc-300">Champions Predictor</b> below.
                </p>
                <div className="mt-2"><LeaderboardExclusionNote /></div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="ucl-champion-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                    <Crown className="w-3 h-3" /> UCL Champion
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick 1 or 2 teams from the seed 1-24 field, locked at Knockout Round Play-offs kickoff. Picking 2 costs you the hedge rate below if one wins.
                </p>
                <div data-testid="ucl-champion-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (1 pick)</label>
                    <input
                        data-testid="ucl-champion-points-input"
                        type="number"
                        value={champPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, ucl_champion_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="ucl-champion-hedge-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (2 picks — hedge)</label>
                    <input
                        data-testid="ucl-champion-hedge-points-input"
                        type="number"
                        value={champHedgePoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, ucl_champion_hedge_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="ucl-darkhorse-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                    <Swords className="w-3 h-3" /> Dark Horse Pick · Per Tie Advanced
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    A seed 9-24 team only (seeds 1-8 are byes), locked alongside UCL Champion. Tiered — stacks for every tie that team advances.
                </p>
                <div data-testid="ucl-darkhorse-outer-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per advance (seed 17-24)</label>
                    <input
                        data-testid="ucl-darkhorse-outer-points-input"
                        type="number"
                        value={dhOuterPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, ucl_darkhorse_outer_tier_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="ucl-darkhorse-inner-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per advance (seed 9-16)</label>
                    <input
                        data-testid="ucl-darkhorse-inner-points-input"
                        type="number"
                        value={dhInnerPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, ucl_darkhorse_inner_tier_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="ucl-advance-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 flex items-center gap-1.5">
                    <Target className="w-3 h-3" /> Two-Leg Predictor · Fixed
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    <b className="text-zinc-300">+3</b> correct / <b className="text-zinc-300">−1</b> incorrect (Knockout + Round of 16) · <b className="text-zinc-300">+6</b> correct / <b className="text-zinc-300">−2</b> incorrect (Quarter/Semi-finals).
                    One pick per 2-leg tie, locks at that tie's Leg 1. Fixed engine values, not editable per tournament.
                </p>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="ucl-champions-predictor-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-300 flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Champions Predictor (Final) · Fixed
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    <b className="text-zinc-300">+10</b> Final winner · <b className="text-zinc-300">+5</b> Regulation/Extra Time/Penalties ·
                    <b className="text-zinc-300"> +5</b> total goals over/under 2.5. Fixed engine values, not editable per tournament.
                </p>
            </div>
            <button
                data-testid="ucl-playoffs-save-scoring-btn"
                onClick={handleSave}
                disabled={isLocked || saving}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
            </button>
        </div>
    );
}

// iter87.374 (user-requested — "follow the NWSL format ... remove
// Power picks") — Dedicated scoring card for NFL Playoffs. Pure
// pick'em now — NO Team Pick Pot, NO Power Pick (both retired; CFB
// Playoffs is unaffected, see `FootballPlayoffsScoringCard` above).
// Super Bowl Champion + Dark Horse Picks points ARE admin-editable
// (same convention as NWSL's card); Playoff Predictor stays a fixed
// engine rule, shown read-only.
function NflPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const champPoints = scoring?.nfl_champion_points ?? 20;
    const champHedgePoints = scoring?.nfl_champion_hedge_points ?? 8;
    const dhPoints = scoring?.nfl_darkhorse_win_points ?? 4;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="nfl-playoffs-scoring-card">
            <div>
                <h2 className="font-display font-extrabold text-xl uppercase" data-testid="nfl-playoffs-scoring-title">
                    Scoring — NFL Playoffs
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                    Pure pick'em — no draft, no rostered teams. Two pre-season mechanics: <b className="text-volt">Super Bowl Champion</b> (1-2 picks,
                    hedge cost if picking 2) and <b className="text-heat">Dark Horse Picks</b> (AFC + NFC wild-card picks, per-win bonus), plus
                    the <b className="text-cyber">Playoff Predictor</b> between rounds (fixed, below).
                </p>
                <div className="mt-2"><LeaderboardExclusionNote /></div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nfl-champion-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                    <Trophy className="w-3 h-3" /> Super Bowl Champion
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick 1 or 2 of the 14 playoff teams, locked at Wild Card Weekend kickoff. Picking 2 costs you the hedge rate below if one wins.
                </p>
                <div data-testid="nfl-champion-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (1 pick)</label>
                    <input
                        data-testid="nfl-champion-points-input"
                        type="number"
                        value={champPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nfl_champion_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="nfl-champion-hedge-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (2 picks — hedge)</label>
                    <input
                        data-testid="nfl-champion-hedge-points-input"
                        type="number"
                        value={champHedgePoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nfl_champion_hedge_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nfl-darkhorse-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                    <Flag className="w-3 h-3" /> Dark Horse Picks · Per Win
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    1 wild-card team (seed 5-7 — not a division winner) per conference, locked alongside Super Bowl Champion. Stacks per conference pick.
                </p>
                <div data-testid="nfl-darkhorse-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per win</label>
                    <input
                        data-testid="nfl-darkhorse-points-input"
                        type="number"
                        value={dhPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, nfl_darkhorse_win_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="nfl-predictor-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Playoff Predictor — Fixed
                </p>
                <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-1">
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · win + cover</span><span className="font-mono font-bold text-volt">+6</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · win, no cover</span><span className="font-mono font-bold text-cyber">+2</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · loss but covers</span><span className="font-mono font-bold text-zinc-400">0</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · miss both</span><span className="font-mono font-bold text-heat">-2</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Over/Under · correct</span><span className="font-mono font-bold text-cyber">+4</span>
                    </li>
                    <li className="flex items-center justify-between py-1">
                        <span>Over/Under · miss (push counts as a miss)</span><span className="font-mono font-bold text-heat">-2</span>
                    </li>
                </ul>
            </div>
            <button
                data-testid="nfl-playoffs-save-scoring-btn"
                onClick={handleSave}
                disabled={isLocked || saving}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
            </button>
        </div>
    );
}

// iter87.437 (user-requested — "mirror NFL exactly") — sibling of
// NflPlayoffsScoringCard above. Dark Horse is ONE pick here (not two
// per-conference) — CFB's 12-team field isn't cleanly 2-way groupable
// like NFL's AFC/NFC.
function CfbPlayoffsScoringCard({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const [saving, setSaving] = useState(false);
    const champPoints = scoring?.cfb_champion_points ?? 20;
    const champHedgePoints = scoring?.cfb_champion_hedge_points ?? 8;
    const dhPoints = scoring?.cfb_darkhorse_win_points ?? 8;
    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateScoring(tournament.id, scoring);
            toast.success("Scoring saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="cfb-playoffs-scoring-card">
            <div>
                <h2 className="font-display font-extrabold text-xl uppercase" data-testid="cfb-playoffs-scoring-title">
                    Scoring — CFB Playoffs
                </h2>
                <p className="text-xs text-zinc-400 leading-relaxed mt-1">
                    Pure pick'em — no draft, no rostered teams. Two pre-season mechanics: <b className="text-volt">National Champion</b> (1-2 picks,
                    hedge cost if picking 2) and <b className="text-heat">Dark Horse Pick</b> (1 team, seed 7-12, per-win bonus), plus
                    the <b className="text-cyber">Playoff Predictor</b> between rounds (fixed, below).
                </p>
                <div className="mt-2"><LeaderboardExclusionNote /></div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="cfb-champion-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-volt flex items-center gap-1.5">
                    <Trophy className="w-3 h-3" /> National Champion
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    Pick 1 or 2 of the 12 playoff teams, locked at First Round kickoff. Picking 2 costs you the hedge rate below if one wins.
                </p>
                <div data-testid="cfb-champion-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (1 pick)</label>
                    <input
                        data-testid="cfb-champion-points-input"
                        type="number"
                        value={champPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, cfb_champion_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
                <div data-testid="cfb-champion-hedge-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points if correct (2 picks — hedge)</label>
                    <input
                        data-testid="cfb-champion-hedge-points-input"
                        type="number"
                        value={champHedgePoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, cfb_champion_hedge_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="cfb-darkhorse-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-heat flex items-center gap-1.5">
                    <Flag className="w-3 h-3" /> Dark Horse Pick · Per Win
                </p>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                    1 team (seed 7-12 only — seeds 1-6 are out), locked alongside National Champion. Stacks per playoff win.
                </p>
                <div data-testid="cfb-darkhorse-points">
                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">Points per win</label>
                    <input
                        data-testid="cfb-darkhorse-points-input"
                        type="number"
                        value={dhPoints}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, cfb_darkhorse_win_points: parseInt(e.target.value) || 0 })}
                        className="brutal-input w-full max-w-[140px] font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                </div>
            </div>
            <div className="brutal-card-light p-3 space-y-2" data-testid="cfb-predictor-scoring-section">
                <p className="text-[10px] uppercase tracking-widest font-bold text-cyber flex items-center gap-1.5">
                    <Zap className="w-3 h-3" /> Playoff Predictor — Fixed
                </p>
                <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-1">
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · win + cover</span><span className="font-mono font-bold text-volt">+6</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · win, no cover</span><span className="font-mono font-bold text-cyber">+2</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · loss but covers</span><span className="font-mono font-bold text-zinc-400">0</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Spread · miss both</span><span className="font-mono font-bold text-heat">-2</span>
                    </li>
                    <li className="flex items-center justify-between border-b border-white/[0.04] py-1">
                        <span>Over/Under · correct</span><span className="font-mono font-bold text-cyber">+4</span>
                    </li>
                    <li className="flex items-center justify-between py-1">
                        <span>Over/Under · miss (push counts as a miss)</span><span className="font-mono font-bold text-heat">-2</span>
                    </li>
                </ul>
            </div>
            <button
                data-testid="cfb-playoffs-save-scoring-btn"
                onClick={handleSave}
                disabled={isLocked || saving}
                className="brutal-btn-primary disabled:opacity-40"
            >
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save scoring"}
            </button>
        </div>
    );
}

// iter86.43.2 — Matchday Boost toggle lives with the other scoring
// levers (it IS a scoring rule); moved here from Tournament Settings.
function MatchdayBoostCard({ tournament, isLocked, onChanged }) {
    const [busy, setBusy] = useState(false);
    const enabled = !!tournament?.matchday_boost?.enabled;
    const toggle = async () => {
        setBusy(true);
        try {
            await api.updateMatchdayBoostSettings(tournament.id, !enabled);
            toast.success(!enabled ? "Matchday Boost is ON ⚡" : "Matchday Boost turned off");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not update Matchday Boost");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className={`brutal-card p-5 space-y-3 ${enabled ? "border-volt bg-volt/5" : "border-[#333]"}`}
            data-testid="matchday-boost-settings-card"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-9 h-9 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${enabled ? "bg-volt" : "bg-zinc-800"}`}>
                        <Zap className={`w-4 h-4 ${enabled ? "text-black" : "text-zinc-500"}`} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                        <p className="font-display font-extrabold text-lg leading-tight">
                            {tournament?.matchday_boost?.scoring_mode === "spread" ? "Weekly Spread Challenge" : "Matchday Boost"}
                        </p>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                            Weekly side game — optional
                        </p>
                    </div>
                </div>
                <button
                    data-testid="matchday-boost-toggle-btn"
                    onClick={toggle}
                    disabled={busy || isLocked}
                    className={`pill shrink-0 font-bold ${enabled ? "bg-volt/20 border-volt text-volt" : "bg-ink border-[#555] text-zinc-400"}`}
                >
                    {busy ? "…" : enabled ? "ON ⚡" : "OFF"}
                </button>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                {tournament?.matchday_boost?.scoring_mode === "spread"
                    ? <>The weekly side game: every week, crewmates pick up to 2 teams against the
                frozen Vegas line from that week's slate. Win AND cover +6 · win without
                covering +2 · loss that covers 0 · miss both −2 (push = not covered). Picks lock
                per game at kickoff; skipping a week costs nothing.</>
                    : <>The weekly side game: every matchday, crewmates pick up to 2 match winners
                from that week's fixtures. Payout is tiered by the picked club's pot —
                win +3 / +5 / +7, draw +1 / +2 / +3 (Pot 1 / 2 / 3+). Picks lock per match
                at kickoff; skipping a week costs nothing.</>}
            </p>
        </div>
    );
}

const DEFAULT_POSITION_POINTS = {
    "1": 20, "2": 17, "3": 15,
    ...Object.fromEntries(Array.from({ length: 7 }, (_, i) => [String(i + 4), 10])),
    ...Object.fromEntries(Array.from({ length: 10 }, (_, i) => [String(i + 11), 5])),
};

export default function ScoringTab({ tournament, scoring, setScoring, isLocked, onChanged }) {
    const scoringNumericEntries = useMemo(
        () => Object.entries(scoring || {}).filter(([, v]) => typeof v === "number"),
        [scoring]
    );
    // iter86.31.3 — Golf position draft: editable scoring knobs.
    const [golfDraft, setGolfDraft] = useState(() => {
        const src = tournament?.golf?.scoring || {};
        return {
            make_cut_points: src.make_cut_points ?? 3,
            champion_pick_points: src.champion_pick_points ?? 10,
            position_points: { ...DEFAULT_POSITION_POINTS, ...(src.position_points || {}) },
        };
    });
    const [golfSaving, setGolfSaving] = useState(false);

    const handleSaveScoring = async () => {
        // iter84.56.6.3 — Zero out fields the UI has greyed out so a
        // stale non-zero value doesn't leak back into the scoring engine.
        // We rebuild `fieldRelevance` here because hooks can't be called
        // inside this handler — the relevance derivation is cheap.
        const stageKeysLocal = new Set((tournament.stages || []).map(s => s.key));
        const firstKey = (tournament.stages || [])[0]?.key || null;
        const reachFields = ["reach_r64_points", "reach_r32_points", "reach_r16_points",
            "reach_qf_points", "reach_sf_points", "reach_final_points"];
        const stageOf = {
            reach_r64_points: "r64", reach_r32_points: "r32", reach_r16_points: "r16",
            reach_qf_points: "qf", reach_sf_points: "sf", reach_final_points: "final",
        };
        const normalized = { ...scoring };
        for (const f of reachFields) {
            const stage = stageOf[f];
            const inStages = stageKeysLocal.has(stage);
            const isEntry = firstKey === stage;
            if (!inStages || isEntry) normalized[f] = 0;
        }
        await api.updateScoring(tournament.id, normalized);
        setScoring(normalized);
        toast.success("Scoring saved");
        onChanged?.();
    };

    // Golf carries its own golf.scoring block — don't require the
    // football top-level scoring doc to render the golf editor.
    if (!scoring && !isGolfFormat(tournament)) return null;

    // iter87.112 — Boost-only templates render a dedicated compact
    // scoring card with a single editable field (picks-per-week).
    // Skips all the season / knockout / KO-decay / Power Pick /
    // MatchdayBoost toggle noise that doesn't apply here.
    if (isBoostOnlyTournament(tournament)) {
        return (
            <BoostOnlyScoringCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.227 — MLB/NBA Playoffs render their own dedicated card
    // (Team Pick Pot + Champions Pick + Series Predictor) instead of
    // falling through to the generic soccer/World-Cup scoring form
    // below (stage decay, third place, group stage — none apply).
    if (isPotPlayoffsTemplate(tournament?.template)) {
        return (
            <PotPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.374 (user-requested) — NFL Playoffs retired Team Pick Pot
    // + Power Pick in favor of a pure pick'em; must be checked BEFORE
    // the generic `isFootballPlayoffsTemplate` dispatch below (which
    // still covers CFB Playoffs, unaffected by this change) since that
    // helper still returns true for NFL too.
    if (tournament?.template === "nfl_playoffs_api") {
        return (
            <NflPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.437 (user-requested — "mirror NFL exactly") — CFB
    // Playoffs followed NFL's lead and retired its own Team Pick Pot
    // too; same reasoning as above — must be checked BEFORE the
    // generic `isFootballPlayoffsTemplate` dispatch, which is now
    // fully unreachable for both templates (kept, not deleted, as the
    // fallback shape if a future football template ever wants it).
    if (tournament?.template === "cfb_playoffs_api") {
        return (
            <CfbPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.230 — NFL/CFB Playoffs render their own dedicated card
    // (round win points + Power Pick). No Champions Pick / Series
    // Predictor cards — those are MLB/NBA-only mechanics.
    if (isFootballPlayoffsTemplate(tournament?.template)) {
        return (
            <FootballPlayoffsScoringCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.361 — NWSL Playoffs render their own dedicated card (pure
    // pick'em, no Team Pick Pot at all).
    if (tournament?.template === "nwsl_playoffs_api") {
        return (
            <NwslPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.465.20 — MLS Playoffs render their own dedicated card
    // (pure pick'em + the Play-In Bonus primitive).
    if (tournament?.template === "mls_playoffs_api") {
        return (
            <MlsPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter87.466.1 — UCL Playoffs render their own dedicated card
    // (pure pick'em + 2-leg Advance Predictor + Final Champions Predictor).
    if (tournament?.template === "ucl_playoffs_api") {
        return (
            <UclPlayoffsScoringCard tournament={tournament} scoring={scoring} setScoring={setScoring} isLocked={isLocked} onChanged={onChanged} />
        );
    }

    // iter86.31.3 — Golf: Pick'em keeps its fixed engine (handicap-only
    // knob). The POSITION DRAFT's make-cut / finish-position / champions
    // pick points are now editable, locking at Round 1 tee-off. Power
    // Pick settings card renders for both golf modes.
    if (isGolfFormat(tournament)) {
        const pickem = isGolfPickem(tournament);
        const golfLocked = isLocked || !!tournament.golf?.r1_started;
        const handleSaveGolfDraftScoring = async () => {
            setGolfSaving(true);
            try {
                await api.updateGolfScoring(tournament.id, golfDraft);
                toast.success("Scoring saved");
                onChanged?.();
            } catch (err) {
                toast.error(err.response?.data?.detail || "Save failed");
            }
            setGolfSaving(false);
        };
        return (
            <>
                <div className="brutal-card p-5 space-y-3" data-testid="golf-scoring-card">
                    <h2 className="font-display font-extrabold text-xl uppercase">
                        Scoring — {pickem ? "Golf Pick'em" : "Golf Position Draft"}
                    </h2>
                    <LeaderboardExclusionNote />
                    {pickem ? (
                        <p className="text-xs text-zinc-400 leading-relaxed" data-testid="golf-scoring-pickem-note">
                            This format has a fixed scoring engine: <b className="text-cyber">lowest average strokes
                            per round actually played wins</b>. Golfers who miss the cut only count their two rounds;
                            withdrawals count whatever rounds they completed. The only setting is the
                            <b className="text-volt"> Power Pick handicap</b> below.
                        </p>
                    ) : (
                        <>
                            <p className="text-xs text-zinc-400 leading-relaxed">
                                Points engine — highest total wins. Tune every value below;
                                they <b className="text-heat">lock when Round 1 tees off</b>.
                            </p>
                            <div className="grid sm:grid-cols-2 gap-3" data-testid="golf-scoring-draft-editor">
                                <div data-testid="golf-scoring-make-cut">
                                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">✂️ Make the cut</label>
                                    <input
                                        data-testid="golf-scoring-make-cut-input"
                                        type="number"
                                        value={golfDraft.make_cut_points}
                                        disabled={golfLocked}
                                        onChange={(e) => setGolfDraft({ ...golfDraft, make_cut_points: parseInt(e.target.value) || 0 })}
                                        className="brutal-input w-full font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                                    />
                                </div>
                                <div data-testid="golf-scoring-champion-pick">
                                    <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">👑 Champions Pick correct</label>
                                    <input
                                        data-testid="golf-scoring-champion-pick-input"
                                        type="number"
                                        value={golfDraft.champion_pick_points}
                                        disabled={golfLocked}
                                        onChange={(e) => setGolfDraft({ ...golfDraft, champion_pick_points: parseInt(e.target.value) || 0 })}
                                        className="brutal-input w-full font-mono disabled:opacity-40 disabled:cursor-not-allowed"
                                    />
                                </div>
                            </div>
                            <div data-testid="golf-scoring-position-points">
                                <p className="text-[10px] uppercase font-bold text-zinc-500 mb-1.5">🏁 Final position points (1st–20th)</p>
                                <div className="grid grid-cols-5 gap-2">
                                    {["1", "2", "3"].map((pos) => (
                                        <div key={pos}>
                                            <label className="block text-[9px] text-zinc-600 font-bold mb-0.5">{pos}º</label>
                                            <input
                                                data-testid={`golf-scoring-position-${pos}-input`}
                                                type="number"
                                                value={golfDraft.position_points[pos]}
                                                disabled={golfLocked}
                                                onChange={(e) => setGolfDraft({
                                                    ...golfDraft,
                                                    position_points: { ...golfDraft.position_points, [pos]: parseInt(e.target.value) || 0 },
                                                })}
                                                className="brutal-input w-full font-mono text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    ))}
                                    {[["4", 4, 10, "4º–10º"], ["11", 11, 20, "11º–20º"]].map(([anchor, from, to, label]) => (
                                        <div key={anchor}>
                                            <label className="block text-[9px] text-zinc-600 font-bold mb-0.5">{label}</label>
                                            <input
                                                data-testid={`golf-scoring-position-${from}-${to}-input`}
                                                type="number"
                                                value={golfDraft.position_points[anchor]}
                                                disabled={golfLocked}
                                                onChange={(e) => {
                                                    const v = parseInt(e.target.value) || 0;
                                                    const bulk = Object.fromEntries(
                                                        Array.from({ length: to - from + 1 }, (_, i) => [String(from + i), v])
                                                    );
                                                    setGolfDraft({
                                                        ...golfDraft,
                                                        position_points: { ...golfDraft.position_points, ...bulk },
                                                    });
                                                }}
                                                className="brutal-input w-full font-mono text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                            />
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[9px] text-zinc-600 mt-1">
                                    Positions 4–10 and 11–20 share one value each. Ties count the position number itself (two golfers at T5 both score 5th-place points).
                                </p>
                            </div>
                            {golfLocked && !isLocked && (
                                <p className="text-[11px] text-heat" data-testid="golf-scoring-locked-note">
                                    Scoring locked — Round 1 has teed off.
                                </p>
                            )}
                            <button
                                data-testid="save-golf-scoring-btn"
                                onClick={handleSaveGolfDraftScoring}
                                disabled={golfLocked || golfSaving}
                                className="brutal-btn-primary disabled:opacity-40"
                            >
                                <Save className="w-4 h-4" /> Save scoring
                            </button>
                        </>
                    )}
                </div>
                <BoostsExtrasSection>
                    <PowerPickSettingsCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
                </BoostsExtrasSection>
            </>
        );
    }

    const stageKeys = new Set((tournament.stages || []).map(s => s.key));
    const hasGroup = stageKeys.has("group") || stageKeys.has("regular");
    // iter84.56.6.3 — Entry-stage reach-points fix.
    // In a knockout-only tournament (no group stage), the FIRST stage is
    // the round where all drafted teams enter, so awarding "reach" points
    // for that stage is meaningless — every drafted team automatically
    // earns it. Grey out the entry-stage reach field so the admin doesn't
    // re-enter a non-zero value by accident.
    const firstStageKey = (tournament.stages || [])[0]?.key || null;
    const isEntryStageReach = (stageKey) => firstStageKey === stageKey;
    const fieldRelevance = {
        win_points: hasGroup, draw_points: hasGroup, loss_points: hasGroup,
        reach_r64_points: stageKeys.has("r64") && !isEntryStageReach("r64"),
        reach_r32_points: stageKeys.has("r32") && !isEntryStageReach("r32"),
        reach_r16_points: stageKeys.has("r16") && !isEntryStageReach("r16"),
        reach_qf_points: stageKeys.has("qf") && !isEntryStageReach("qf"),
        reach_sf_points: stageKeys.has("sf") && !isEntryStageReach("sf"),
        reach_final_points: stageKeys.has("final") && !isEntryStageReach("final"),
        bronze_points: !!tournament.count_third_place_playoff,
        champion_points: stageKeys.has("champion"),
        bonus_pre_knockout: stageKeys.has("r64") || stageKeys.has("r32") || stageKeys.has("r16") || stageKeys.has("qf") || stageKeys.has("sf") || stageKeys.has("final"),
        bonus_after_r64: stageKeys.has("r64"),
        bonus_after_r32: stageKeys.has("r32"),
        bonus_after_r16: stageKeys.has("r16"),
        bonus_after_qf: stageKeys.has("qf"),
        bonus_after_sf: stageKeys.has("sf"),
    };
    const fieldLabels = {
        bonus_pre_knockout: "Before start of Knockout Stage",
        bonus_after_r64: "Before start of R32",
        bonus_after_r32: "Before start of R16",
        bonus_after_r16: "Before start of QF",
        bonus_after_qf: "Before start of SF",
        bonus_after_sf: "Before start of Final",
        bronze_points: "Bronze (3rd place) bonus",
        // iter87.114 — NFL Season uses "tie" terminology, not "draw".
        ...(tournament?.template === "nfl_season_api" ? { draw_points: "Tie points" } : {}),
    };
    const GROUP_FIELDS = ["win_points", "draw_points", "loss_points"];
    // iter87.114 — Football (NFL Season) uses "tie", not "draw"; and the
    // header should say football/regular-season, not soccer/season-matches.
    const isNflSeason = tournament?.template === "nfl_season_api";
    const groupHeaderLabel = isNflSeason
        ? "🏈 Regular Season · Win / Tie / Loss"
        : (isSeasonFormat(tournament) ? "⚽ Season Matches · Win / Draw / Loss" : "⚽ Drafted Team · Group Stage");
    const KNOCKOUT_FIELDS = [
        "reach_r64_points", "reach_r32_points", "reach_r16_points",
        "reach_qf_points", "reach_sf_points", "reach_final_points",
        "bronze_points", "champion_points",
    ];
    const BONUS_FIELDS = [
        "bonus_pre_knockout", "bonus_after_r64", "bonus_after_r32",
        "bonus_after_r16", "bonus_after_qf", "bonus_after_sf",
    ];
    const fieldByKey = Object.fromEntries(scoringNumericEntries);
    const reachStageOf = {
        reach_r64_points: "r64", reach_r32_points: "r32", reach_r16_points: "r16",
        reach_qf_points: "qf", reach_sf_points: "sf", reach_final_points: "final",
    };
    const renderField = (key) => {
        const val = fieldByKey[key];
        if (val === undefined) return null;
        const relevant = fieldRelevance[key] !== false;
        // iter84.56.6.3 — Distinguish the two reasons a reach-points field
        // is greyed out: (a) the stage isn't in this tournament's stages
        // OR (b) the stage IS in stages but it's the entry round.
        const reachStage = reachStageOf[key];
        const isEntryReach = !relevant && reachStage && firstStageKey === reachStage;
        const greyedReason = isEntryReach
            ? `${reachStage.toUpperCase()} is this tournament's first round — every drafted team starts here, so reaching it earns no points`
            : "not in this tournament's stages";
        return (
            <div key={key} data-testid={`scoring-field-${key}`} className={relevant ? "" : "opacity-40"}>
                <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">{fieldLabels[key] || key.replace(/_/g, " ")}</label>
                <input
                    data-testid={`scoring-${key}-input`}
                    type="number"
                    value={relevant ? val : 0}
                    disabled={!relevant || isLocked}
                    onChange={(e) => setScoring({ ...scoring, [key]: parseInt(e.target.value) || 0 })}
                    className={`brutal-input w-full font-mono ${relevant && !isLocked ? "" : "cursor-not-allowed"}`} />
                {!relevant && (
                    <p
                        data-testid={`scoring-field-${key}-greyed-reason`}
                        data-entry-reach={isEntryReach ? "true" : "false"}
                        className="text-[9px] text-zinc-600 mt-0.5"
                    >
                        {greyedReason}
                    </p>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="brutal-card p-5 space-y-4">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <h2 className="font-display font-extrabold text-xl uppercase">Scoring Rules</h2>
                    <p className="text-xs text-zinc-500" data-testid="scoring-format-hint">
                        Showing fields relevant to <b className="text-cyber">{tournament.template === "custom" ? `custom · ${tournament.format}` : tournament.template.replace(/_/g, " ")}</b>. Irrelevant rounds are grayed out.
                    </p>
                </div>
                <LeaderboardExclusionNote />
                {/* iter86.42.1 — Cumulative advancement is a KO concept
                    (stage-reach point stacking). Season formats have no
                    stages to stack — hide the toggle. */}
                {!isSeasonFormat(tournament) && (
                <div
                    className="brutal-card-light p-3 flex items-start gap-3"
                    data-testid="scoring-cumulative-toggle-wrapper"
                >
                    <input
                        id="cumulative-toggle"
                        type="checkbox"
                        data-testid="scoring-cumulative-toggle"
                        checked={scoring.cumulative_advancement !== false}
                        disabled={isLocked}
                        onChange={(e) => setScoring({ ...scoring, cumulative_advancement: e.target.checked })}
                        className="mt-1 w-4 h-4 accent-cyber cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    />
                    <label htmlFor="cumulative-toggle" className="flex-1 cursor-pointer">
                        <span className="block text-sm font-bold text-zinc-200">
                            Cumulative advancement scoring
                        </span>
                        <span className="block text-xs text-zinc-500 mt-0.5">
                            <b className="text-zinc-400">Checked (default):</b> a team that reaches the SF earns r32 + r16 + qf + sf points combined.<br />
                            <b className="text-zinc-400">Unchecked (final-position):</b> only the team's final landing spot counts — SF reach = sf points only.
                        </span>
                    </label>
                </div>
                )}
                <div className="space-y-4" data-testid="scoring-form-grouped">
                    <div className="brutal-card-light p-3 space-y-2">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-cyber" data-testid="scoring-group-header">
                            {groupHeaderLabel}
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {GROUP_FIELDS.map(renderField)}
                        </div>
                    </div>
                    {/* iter86.42.1 — Season formats have NO knockout stage:
                        instead of 8 greyed-out KO rows, hide the whole KO
                        section and give champion points its own card. */}
                    {isSeasonFormat(tournament) ? (
                        <div className="brutal-card-light p-3 space-y-2" data-testid="scoring-season-champion-section">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-blaze">
                                {tournament?.template === "nfl_season_api" ? "🏆 Champions Pick · Best Record" : "🏆 League Champion"}
                            </p>
                            <p className="text-[10px] text-zinc-500 leading-relaxed">
                                {tournament?.template === "nfl_season_api"
                                    ? "Champions Pick bonus — awarded when the season completes to every crewmate whose nominated team finishes with the best record in the NFL (ties: each correct picker banks it). There are no knockout rounds in a season format."
                                    : "Bonus added for holding the club that wins the league, awarded when the season completes. There are no knockout rounds in a season format."}
                            </p>
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {renderField("champion_points")}
                            </div>
                        </div>
                    ) : (
                    <div className="brutal-card-light p-3 space-y-2">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-blaze">🏆 Drafted Team · Knockout Stage</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {KNOCKOUT_FIELDS.map(renderField)}
                        </div>
                    </div>
                    )}
                    {/* iter86.21 — Champion Bonus Pick decay schedule is a
                        KO-format concept (points decrease as stages
                        progress). Season formats have no KO, so hide the
                        whole subsection instead of leaving 6 greyed-out
                        rows that confuse admins. */}
                    {!isSeasonFormat(tournament) && (
                    <div
                        data-testid="scoring-bonus-decay-section"
                        className="brutal-card-light p-3 space-y-2"
                    >
                        <p className="text-[10px] uppercase tracking-widest font-bold text-volt">👑 Champion Bonus Pick · Decay schedule</p>
                        <p className="text-[10px] text-zinc-500 leading-relaxed">
                            Points your champion pick is worth based on WHEN you lock it in. Earlier picks = more points.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {BONUS_FIELDS.map(renderField)}
                        </div>
                    </div>
                    )}
                </div>
                <button data-testid="save-scoring-btn" onClick={handleSaveScoring} disabled={isLocked} className="brutal-btn-primary disabled:opacity-40">
                    <Save className="w-4 h-4" /> Save scoring
                </button>
            </div>
            {/* iter87.199.79 — Grouped BOOSTS & EXTRAS section. All the
                optional-on-top-of-core scoring levers (3rd-place playoff,
                Matchday Boost toggle, Power Pick) share a common section
                header + divider so the tab reads as `Core → Extras`
                instead of an ad-hoc stack. */}
            <BoostsExtrasSection>
                {/* iter86.21 — Third-place playoff is a KO-format concept
                    (bronze match between beaten semifinalists). Season
                    formats never have a semifinal, so the toggle is
                    meaningless there — hide it entirely. */}
                {!isSeasonFormat(tournament) && (
                    <ThirdPlacePlayoffCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
                )}
                {/* iter86.43.2 — Matchday Boost is a scoring lever: soccer
                    season formats only. */}
                {isSeasonFormat(tournament) &&
                    (tournament?.matchday_boost || tournament?.api_football_league_id) && (
                    <MatchdayBoostCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
                )}
                <PowerPickSettingsCard tournament={tournament} isLocked={isLocked} onChanged={onChanged} />
            </BoostsExtrasSection>
        </>
    );
}
